import { t } from '@glory/i18n';
import {
  Button,
  cn,
  IconButton,
  notify,
  notifyError,
  Segments,
  Spinner,
  TextAreaInput,
  TRANSITION,
} from '@glory/ui';
import { Crop, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';

import { AreaSelect } from './area-select.tsx';
import { type Area, captureArea, FEEDBACK_IGNORE_ATTR, type Shot } from './capture.ts';

type Category = 'bug' | 'idea' | 'ux' | 'other';
type Mode = 'closed' | 'open' | 'selecting' | 'capturing';

const CATEGORIES: readonly Category[] = ['bug', 'idea', 'ux', 'other'];
const ignore = { [FEEDBACK_IGNORE_ATTR]: '' };
const OPEN_EVENT = 'glory:feedback:open';

export function openFeedbackWidget() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/**
 * Отзыв тестировщика поверх любого экрана: категория, описание и снимок выделенной области.
 * Отзывы уходят в `/api/feedback`; читает их владелец на отдельной странице.
 */
export function FeedbackWidget() {
  const [mode, setMode] = useState<Mode>('closed');
  const [category, setCategory] = useState<Category>('bug');
  const [text, setText] = useState('');
  const [shot, setShot] = useState<Shot | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dropShot = useCallback(() => {
    setShot((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  useEffect(() => {
    const open = () => {
      setMode('open');
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => {
      window.removeEventListener(OPEN_EVENT, open);
    };
  }, []);

  useEffect(() => {
    if (mode !== 'open') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMode('closed');
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [mode]);

  const onSelect = useCallback(
    (area: Area) => {
      setMode('capturing');
      // Слой выделения успевает исчезнуть до снимка.
      requestAnimationFrame(() => {
        void captureArea(area)
          .then((next) => {
            dropShot();
            setShot(next);
          })
          .catch(() => {
            notifyError(t('feedback.shotFailed'));
          })
          .finally(() => {
            setMode('open');
          });
      });
    },
    [dropShot],
  );
  const onCancelSelect = useCallback(() => {
    setMode('open');
  }, []);

  const send = async () => {
    if (!text.trim()) {
      setError(t('feedback.empty'));
      return;
    }
    setSending(true);
    const form = new FormData();
    form.set('category', category);
    form.set('description', text.trim());
    form.set('pageUrl', window.location.href);
    form.set('viewport', `${String(window.innerWidth)}×${String(window.innerHeight)}`);
    if (shot) {
      form.set('screenshot', shot.blob, shot.blob.type === 'image/png' ? 'shot.png' : 'shot.jpg');
      form.set('width', String(shot.width));
      form.set('height', String(shot.height));
    }
    try {
      const res = await fetch('/api/feedback', { method: 'POST', body: form, credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      notify(t('feedback.sent'));
      setText('');
      dropShot();
      setMode('closed');
    } catch {
      notifyError(t('feedback.failed'));
    } finally {
      setSending(false);
    }
  };

  const panelOpen = mode === 'open' || mode === 'capturing';

  return (
    <div {...ignore}>
      <AnimatePresence>
        {panelOpen ? (
          <motion.section
            key="panel"
            role="dialog"
            aria-label={t('feedback.title')}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98, transition: TRANSITION.exit }}
            transition={TRANSITION.enter}
            className="fixed right-4 bottom-4 z-[2147482000] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-3 rounded-xl border-thick border-border bg-surface p-4 text-text shadow-float print:hidden"
          >
            <header className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold">{t('feedback.title')}</h2>
                <p className="mt-0.5 text-sm text-muted">{t('feedback.lead')}</p>
              </div>
              <IconButton
                label={t('feedback.close')}
                icon={<X />}
                size="sm"
                onClick={() => {
                  setMode('closed');
                }}
              />
            </header>
            <Segments
              aria-label={t('feedback.category')}
              stretch
              size="sm"
              value={category}
              onValueChange={setCategory}
              options={CATEGORIES.map((value) => ({ value, label: t(`feedback.categories.${value}`) }))}
            />
            <div className="flex flex-col gap-1">
              <TextAreaInput
                aria-label={t('feedback.description')}
                placeholder={t('feedback.placeholder')}
                value={text}
                rows={4}
                maxHeight={240}
                maxLength={5000}
                autoFocus
                onValueChange={(v) => {
                  setText(v);
                  if (error) setError(null);
                }}
              />
              {error ? <p className="text-sm text-danger">{error}</p> : null}
            </div>
            {shot ? (
              <div className="relative overflow-hidden rounded-md border border-border bg-surface-2">
                <img src={shot.url} alt={t('feedback.shotAlt')} className="max-h-40 w-full object-contain" />
                <IconButton
                  label={t('feedback.removeShot')}
                  icon={<X />}
                  size="sm"
                  variant="secondary"
                  className="absolute top-1.5 right-1.5"
                  onClick={dropShot}
                />
              </div>
            ) : null}
            <footer className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={mode === 'capturing'}
                onClick={() => {
                  setMode('selecting');
                }}
              >
                {mode === 'capturing' ? <Spinner /> : <Crop />}
                {mode === 'capturing'
                  ? t('feedback.capturing')
                  : shot
                    ? t('feedback.recapture')
                    : t('feedback.capture')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                className={cn('ml-auto')}
                disabled={sending || mode === 'capturing'}
                onClick={() => {
                  void send();
                }}
              >
                {sending ? t('feedback.sending') : t('feedback.send')}
              </Button>
            </footer>
          </motion.section>
        ) : null}
      </AnimatePresence>
      {mode === 'selecting' ? <AreaSelect onSelect={onSelect} onCancel={onCancelSelect} /> : null}
    </div>
  );
}
